"use client";

import type { AssistantStaticBubbleVariantProps, UserStaticBubbleVariantProps } from "@ui/components/core/BaseChat";
import { MessagesList } from "@ui/components/core/BaseChat";
import { clsx } from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import { getMessageAlignmentClassName } from "./alignment";
import { AssistantScenarioMessage, StaticMessage, UserScenarioMessage } from "./messageRenderers";
import type { ChatScenarioData } from "./types";

const defaultAssistantMessageVariants: AssistantStaticBubbleVariantProps = { variant: "cream" };
const defaultUserMessageVariants: UserStaticBubbleVariantProps = { variant: "elevated" };

interface ChatScenarioProps {
  scenario: ChatScenarioData;
  active?: boolean;
  className?: string;
  anchor?: "top" | "bottom";
  onComplete?: () => void;
  additionalMessageDelayMS?: number;
  additionalPartDelayMS?: number;
  assistantMessageVariants?: AssistantStaticBubbleVariantProps;
  userMessageVariants?: UserStaticBubbleVariantProps;
}

export function ChatScenario({
  scenario,
  active = false,
  className,
  anchor = "top",
  onComplete,
  additionalMessageDelayMS = 350,
  additionalPartDelayMS = 300,
  assistantMessageVariants = defaultAssistantMessageVariants,
  userMessageVariants = defaultUserMessageVariants,
}: ChatScenarioProps): JSX.Element {
  const [visibleMessageCount, setVisibleMessageCount] = useState(0);
  const [playbackKey, setPlaybackKey] = useState(0);
  const onCompleteRef = useRef(onComplete);
  const playbackRunIdRef = useRef(0);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const handleMessageComplete = useCallback(
    (messageIndex: number, playbackRunId: number) => {
      if (playbackRunId !== playbackRunIdRef.current) {
        return;
      }

      const isLastMessage = messageIndex === scenario.messages.length - 1;

      if (isLastMessage) {
        onCompleteRef.current?.();
        return;
      }

      const nextVisibleMessageCount = messageIndex + 2;

      const timeout = setTimeout(() => setVisibleMessageCount(nextVisibleMessageCount), additionalMessageDelayMS);

      return () => {
        clearTimeout(timeout);
      };
    },
    [scenario.messages, additionalMessageDelayMS],
  );

  useEffect(() => {
    const nextPlaybackRunId = playbackRunIdRef.current + 1;

    playbackRunIdRef.current = nextPlaybackRunId;
    setPlaybackKey(nextPlaybackRunId);

    if (!active) {
      setVisibleMessageCount(0);
      return;
    }

    if (scenario.messages.length === 0) {
      setVisibleMessageCount(0);
      onCompleteRef.current?.();
      return;
    }

    setVisibleMessageCount(1);
  }, [active, scenario.messages]);

  const visibleMessages = scenario.messages.slice(0, visibleMessageCount);

  return (
    <MessagesList
      anchor={anchor}
      className={clsx("tw-overflow-hidden", className)}
      contentClassName='tw-overflow-hidden tw-p-1.5'
    >
      {scenario.initialMessages?.map((msg, idx) => (
        <StaticMessage
          key={msg.id ?? idx}
          message={msg}
          assistantMessageVariants={assistantMessageVariants}
          userMessageVariants={userMessageVariants}
        />
      ))}

      {visibleMessages.map((msg, index) => (
        <div key={`${playbackKey}-${msg.id ?? index}`} className={getMessageAlignmentClassName(msg.role)}>
          {msg.role === "user" ? (
            <UserScenarioMessage
              assistantMessageVariants={assistantMessageVariants}
              message={msg}
              messageIndex={index}
              playbackRunId={playbackKey}
              onMessageComplete={handleMessageComplete}
              userMessageVariants={userMessageVariants}
            />
          ) : (
            <AssistantScenarioMessage
              assistantMessageVariants={assistantMessageVariants}
              message={msg}
              messageIndex={index}
              playbackRunId={playbackKey}
              onMessageComplete={handleMessageComplete}
              partDelayMS={additionalPartDelayMS}
              userMessageVariants={userMessageVariants}
            />
          )}
        </div>
      ))}
    </MessagesList>
  );
}
